/**
 * The microphone: whether we may have one, asking for it, and holding it.
 *
 * The rule the design cares about most is *when* the question is asked. Nobody
 * is asked for a microphone by a game they have not met anyone in, so nothing
 * here happens on load: `askIfNeeded` is called the first time another player
 * comes within `VOICE_NEAR`, and at most once ever. An explicit "no" is written
 * to `localStorage` and is final — the prompt is never shown again on this
 * machine. Dismissing it without answering is not a no; it is suppressed for
 * the rest of the session and may be offered once more another day.
 *
 * The second rule is that the browser's own permission dialog must not strand
 * the player. See `beginCapture` for the pointer-lock dance, which is the whole
 * reason this file knows the game exists at all.
 *
 * Everything degrades to silence. No `mediaDevices`, an insecure origin, no
 * microphone hardware, a refusal, an exception — all of them end with
 * `stream === null` and a game that plays exactly as it did before.
 */

import type { EventBus } from '../core/types';

/** What the player has already told us. Absent from storage means "not asked". */
export type MicDecision = 'granted' | 'denied';

/** Where the answer lives. Its own key: this is a device permission, not a game setting. */
const STORE_KEY = 'backrooms.voice.mic';

/** Seconds the prompt waits for an answer before withdrawing the question. */
const PROMPT_TIMEOUT = 25;

/**
 * Constraints aimed at a headset in a room with a game playing out loud. Echo
 * cancellation is the one that matters: without it, everyone hears the whole
 * building twice.
 */
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

export interface MicHooks {
  /** Fired when a live track first arrives, so the mesh can offer it around. */
  onOpened(): void;
  /** Toasts and subtitles. Null before the first frame has handed us a bus. */
  bus(): EventBus | null;
}

/** True when this browser could conceivably give us a microphone. */
export function micSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export class Microphone {
  /** The live capture, or null. The only thing the rest of the mesh reads. */
  stream: MediaStream | null = null;

  private decision: MicDecision | null = readDecision();
  /** Set when the player waved the prompt away rather than answering it. */
  private deferred = false;
  private asking = false;
  private capturing = false;
  private muted = false;
  private prompt: Prompt | null = null;

  constructor(private readonly hooks: MicHooks) {}

  /** True once nothing we do could ever produce a microphone prompt again. */
  get settled(): boolean {
    return this.decision !== null || this.deferred || !micSupported();
  }

  get available(): boolean {
    return this.stream !== null;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Somebody is close enough to talk to. Asks at most once; on a machine that
   * has already said yes it silently reopens the capture, since the browser
   * remembers the grant and will not show anything.
   */
  askIfNeeded(): void {
    if (!micSupported() || this.capturing || this.stream) return;
    if (this.decision === 'denied' || this.deferred) return;
    if (this.decision === 'granted') {
      // Previously granted: no dialog will appear, so there is nothing to
      // protect the player from and no reason to interrupt them.
      void this.beginCapture(false);
      return;
    }
    if (this.asking) return;
    this.asking = true;
    this.prompt = new Prompt(
      () => this.answer(true),
      () => this.answer(false),
      () => this.answer(null),
      PROMPT_TIMEOUT,
    );
  }

  /** Toggle the transmit mute. Silent no-op when there is no microphone. */
  toggleMute(): void {
    if (!this.stream) return;
    this.muted = !this.muted;
    for (const t of this.stream.getAudioTracks()) t.enabled = !this.muted;
    this.hooks.bus()?.emit('ui:toast', {
      text: this.muted ? 'Microphone muted' : 'Microphone live',
      tone: this.muted ? 'info' : 'good',
    });
  }

  /** Per-frame housekeeping: the prompt's own timeout. */
  update(dt: number): void {
    this.prompt?.update(dt);
  }

  dispose(): void {
    this.prompt?.close();
    this.prompt = null;
    this.release();
  }

  /** Stop the capture and let the browser drop the recording indicator. */
  release(): void {
    const s = this.stream;
    this.stream = null;
    this.capturing = false;
    if (!s) return;
    for (const t of s.getTracks()) {
      try {
        t.stop();
      } catch {
        /* already ended */
      }
    }
  }

  /* ---------------------------------------------------------------- *
   * The question
   * ---------------------------------------------------------------- */

  /** `null` is "dismissed", which is deliberately not the same as "no". */
  private answer(yes: boolean | null): void {
    this.prompt?.close();
    this.prompt = null;
    this.asking = false;

    if (yes === null) {
      this.deferred = true;
      return;
    }
    if (!yes) {
      this.decision = 'denied';
      writeDecision('denied');
      this.hooks.bus()?.emit('ui:toast', { text: 'Voice chat off. Nobody will hear you speak.' });
      return;
    }
    void this.beginCapture(true);
  }

  /**
   * `interactive` means a permission dialog is genuinely possible, which is the
   * only case that needs the pointer-lock dance below.
   *
   * Every browser this game runs on drops pointer lock to show a permission
   * prompt, and a lock lost while the simulation is running leaves the player
   * standing in a corridor unable to turn — the exact bug the click-to-recover
   * path in `core/input.ts` was added for. Rather than rely on the browser
   * doing it, we take the pointer back ourselves, first, and deliberately do it
   * with the plain DOM call rather than `InputAPI.releaseLock()`: `releaseLock`
   * marks the release as the game's own and *suppresses* the synthetic Escape,
   * which is precisely the thing we want to happen here. Losing it this way is
   * indistinguishable from the player pressing Escape, so `core/input.ts` raises
   * `pause`, `game.ts` opens the pause menu, and there is a Resume button on
   * screen behind the dialog whatever the player answers.
   *
   * `getUserMedia` is called in the same task as the click or keypress that got
   * us here so the gesture is still live.
   */
  private async beginCapture(interactive: boolean): Promise<void> {
    if (this.capturing || this.stream || !micSupported()) return;
    this.capturing = true;

    if (interactive && typeof document !== 'undefined' && document.pointerLockElement) {
      try {
        document.exitPointerLock();
      } catch {
        /* nothing locked, or the browser refused; the dialog is next either way */
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: false });
      if (!this.capturing) {
        // Disposed while the dialog was up.
        for (const t of stream.getTracks()) t.stop();
        return;
      }
      this.stream = stream;
      this.muted = false;
      this.decision = 'granted';
      writeDecision('granted');
      if (interactive) {
        this.hooks.bus()?.emit('ui:toast', { text: 'Microphone on. V to mute.', tone: 'good' });
      }
      this.hooks.onOpened();
    } catch (err) {
      this.capturing = false;
      // A refusal and a machine with no microphone in it are the same outcome
      // from here: there is nothing to ask again for, so neither is ever asked
      // again. Anything else (a device in use, a driver fault) is left unsettled
      // so a later session can retry.
      const name = (err as { name?: string } | null)?.name ?? '';
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
        this.decision = 'denied';
        writeDecision('denied');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
        this.decision = 'denied';
        writeDecision('denied');
        if (interactive) this.hooks.bus()?.emit('ui:toast', { text: 'No microphone found.' });
      } else {
        this.deferred = true;
      }
    }
  }
}

/* ------------------------------------------------------------------ *
 * Storage
 * ------------------------------------------------------------------ */

function readDecision(): MicDecision | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw === 'granted' || raw === 'denied' ? raw : null;
  } catch {
    // Private mode or a blocked origin. Unknowable is the same as unasked.
    return null;
  }
}

function writeDecision(d: MicDecision): void {
  try {
    localStorage.setItem(STORE_KEY, d);
  } catch {
    /* nothing to do; the worst case is being asked once more next session */
  }
}

/* ------------------------------------------------------------------ *
 * The prompt
 *
 * Its own DOM rather than a screen in `ui/`, because it is not a screen: it has
 * to be answerable *without* surrendering the pointer, or asking the question
 * would itself cause the bug it is trying to avoid. So it takes the keyboard
 * (Y/N, which nothing in `core/input.ts` binds) and also accepts clicks for the
 * case where the pointer is already free.
 * ------------------------------------------------------------------ */

const FONT = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";

class Prompt {
  private readonly root: HTMLElement;
  private left: number;
  private closed = false;

  constructor(
    private readonly onYes: () => void,
    private readonly onNo: () => void,
    private readonly onDismiss: () => void,
    timeout: number,
  ) {
    this.left = timeout;
    this.root = document.createElement('div');
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Microphone');
    this.root.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:14vh',
      'transform:translateX(-50%)',
      'z-index:40',
      'max-width:min(48ch,88vw)',
      'padding:0.9em 1.1em',
      'background:var(--panel,rgba(9,8,6,0.94))',
      'border:1px solid var(--line,rgba(164,149,78,0.34))',
      'color:var(--amber,#e8d98a)',
      `font-family:${FONT}`,
      'font-size:clamp(12px,0.6vw + 8px,17px)',
      'letter-spacing:0.06em',
      'line-height:1.5',
      'text-align:center',
      'pointer-events:auto',
      'user-select:none',
    ].join(';');

    const line = document.createElement('div');
    line.textContent = 'Someone else is here.';
    const sub = document.createElement('div');
    sub.style.cssText = 'color:var(--amber-dim,#a4954e);margin-top:0.35em';
    sub.textContent = 'Use your microphone so they can hear you?';

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:0.6em;justify-content:center;margin-top:0.9em';
    row.append(this.button('[Y] Yes', this.onYes), this.button('[N] No', this.onNo));

    this.root.append(line, sub, row);
    document.body.appendChild(this.root);
    window.addEventListener('keydown', this.onKey, true);
  }

  update(dt: number): void {
    if (this.closed) return;
    this.left -= dt;
    // An unanswered question is withdrawn rather than answered for them; the
    // caller treats that as "not now", never as a refusal.
    if (this.left <= 0) this.onDismiss();
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    window.removeEventListener('keydown', this.onKey, true);
    this.root.remove();
  }

  private button(label: string, action: () => void): HTMLElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.style.cssText = [
      'background:transparent',
      'border:1px solid var(--line,rgba(164,149,78,0.34))',
      'color:var(--amber-dim,#a4954e)',
      `font:inherit`,
      'letter-spacing:inherit',
      'padding:0.35em 1.1em',
      'cursor:pointer',
    ].join(';');
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      action();
    });
    b.addEventListener('pointerenter', () => (b.style.color = 'var(--amber,#e8d98a)'));
    b.addEventListener('pointerleave', () => (b.style.color = 'var(--amber-dim,#a4954e)'));
    return b;
  }

  /**
   * Capture phase, so the answer never also reaches the game. Escape is left
   * alone on purpose — it belongs to the pause menu, and stealing it would be
   * a second way to strand somebody.
   */
  private readonly onKey = (e: KeyboardEvent): void => {
    if (this.closed || e.repeat || e.altKey || e.ctrlKey || e.metaKey) return;
    if (isTyping()) return;
    if (e.code === 'KeyY') {
      e.preventDefault();
      e.stopPropagation();
      this.onYes();
    } else if (e.code === 'KeyN') {
      e.preventDefault();
      e.stopPropagation();
      this.onNo();
    }
  };
}

/** True while the player is writing something rather than playing. */
export function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}
